import * as Yup from 'yup';
import Swal from 'sweetalert2';

const DeductionSchema = Yup.object().shape({
    name: Yup.string().required('Deduction name is required').trim(),
    cost_per_action: Yup.number()
        .min(0, 'Cost must be positive')
        .required('Cost per action is required'),
    number_of_actions: Yup.number()
        .min(1, 'Number of actions must be at least 1')
        .required('Number of actions is required'),
    invoice: Yup.object().shape({
        url: Yup.string().trim().nullable(),
        description: Yup.string().trim().nullable(),
    }).nullable(),
    // The approved invoice that paid for the work — optional, since in-house
    // work is never invoiced.
    invoice_ref: Yup.object().shape({
        invoice_id: Yup.string().nullable(),
    }).nullable(),
});

const ApprovalSchema = Yup.object().shape({
    email: Yup.string()
        .email('Invalid email format')
        .lowercase()
        .trim()
        .optional(),
    status: Yup.string()
        .oneOf(['pending', 'approved', 'rejected'], 'Invalid status')
        .default('pending'),
    signature: Yup.string().default('').nullable(),
    comments: Yup.string().default('').nullable(),
    updated_at: Yup.date().nullable(),
});

const AuditorApprovalSchema = Yup.object().shape({
    email: Yup.string()
        .email('Invalid email format')
        .lowercase()
        .trim()
        .nullable(),
    status: Yup.string()
        .oneOf(['pending', 'approved', 'rejected'], 'Invalid status')
        .default('pending'),
    signature: Yup.string().default('').nullable(),
    comments: Yup.string().default('').nullable(),
    updated_at: Yup.date().nullable(),
});

const AddressSchema = Yup.object().shape({
    street: Yup.string().required('Street address is required').trim(),
    city: Yup.string().required('City is required').trim(),
    province: Yup.string().required('Province is required').trim(),
    postal_code: Yup.string().required('Postal code is required').trim(),
    country: Yup.string().required('Country is required').trim(),
});

const EmailSchema = Yup.object().shape({
    name: Yup.string().required('Recipient name is required').trim(),
    email: Yup.string()
        .email('Invalid email format')
        .lowercase()
        .trim()
        .required('Recipient email is required'),
    cc: Yup.array()
        .of(Yup.string().email('Invalid CC email format').lowercase().trim())
        .default([]),
    attachments: Yup.array()
        .of(Yup.string().trim())
        .default([]),
    notes: Yup.string().trim().nullable(),
});

const TenantSchema = Yup.object().shape({
    name: Yup.string().required('Tenant name can’t be empty').trim(),
    email: Yup.string()
        .trim()
        .when('$index', (index, schema) =>
            index === 0
                ? schema
                    .lowercase()
                    .email('Invalid email format')
                    .required('First tenant email can’t be empty')
                : schema
                    .test(
                        'is-email-or-na',
                        'Must be a valid email or "N/A"',
                        (value) => {
                            if (!value) return false;
                            if (value === 'N/A') return true;
                            return Yup.string().lowercase().email().isValidSync(value);
                        }
                    )
                    .required('Tenant email or "N/A" can\'t be empty')
        ),
});

const PropertyDetailsSchema = Yup.object().shape({
    property_id: Yup.string().required('Property selection is required').trim(),
    building_name: Yup.string().required('Building name is required').trim(),
    unit_no: Yup.string().required('Unit number is required').trim(),
});

const RentalPeriodSchema = Yup.object().shape({
    start_date: Yup.date()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .required('Start date is required'),
    end_date: Yup.date()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .required('End date is required')
        .min(Yup.ref('start_date'), 'End date must be after start date'),
});

const AccountDetailsSchema = Yup.object().shape({
    security: Yup.number()
        .moreThan(0, 'Security deposit must be greater than 0')
        .required('Security deposit is required'),
    interest: Yup.number()
        .min(0, 'Interest must be 0 or greater')
        .notRequired()
        .nullable(),
    interest: Yup.number()
        .min(0, 'Prorate amount cannot be negative')
        .notRequired()
        .nullable(),
    deductions: Yup.array().of(DeductionSchema).default([]),
});

const StatementDetailsSchema = Yup.object().shape({
    mailed_on: Yup.string().required('Mailed date is required').trim(),
    address: Yup.string().trim().nullable(),
});

const LandlordDetailsSchema = Yup.object().shape({
    name: Yup.string().required('Landlord name is required').trim(),
    address: Yup.string().required('Landlord address is required').trim(),
    phone: Yup.string().required('Phone number is required').trim(),
    manager_id: Yup.string().optional(),
    signature: Yup.string().required('Signature is required').trim(),
});

const StatementOfAccountValidationSchema = Yup.object().shape({
    tenant: Yup.array()
        .of(TenantSchema)
        .min(1, 'At least one tenant is required')
        .test(
            'at-least-one-valid-email',
            'At least one tenant must provide a valid email address (not "N/A")',
            (tenants) =>
                tenants.some(
                    (tenant) =>
                        tenant.email &&
                        tenant.email !== 'N/A' &&
                        tenant.email !== 'n/a' &&
                        Yup.string().email().isValidSync(tenant.email)
                )
        ),
    address: AddressSchema.required('Address is required'),
    rental_period: RentalPeriodSchema.required('Rental period is required'),
    property_details: PropertyDetailsSchema.required('Property details are required'),
    account_details: AccountDetailsSchema.required('Account details are required'),
    statement_details: StatementDetailsSchema.required('Statement details are required'),
    landlord_details: LandlordDetailsSchema.required('Landlord details are required'),
    email_details: EmailSchema.nullable(), // Optional per schema
    approvals: Yup.array().of(ApprovalSchema).default([]),
    auditor_approval: AuditorApprovalSchema.required('Auditor approval is required'),
});

const getOrdinal = (index) => {
    const ordinals = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'];
    return ordinals[index] || `${index + 1}th`;
};

const prettifyPath = (path) => {
    if (!path) return 'Field';

    const parts = path.split(/[\[\].]/).filter(Boolean);
    let result = '';
    let currentContext = '';

    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const nextPart = parts[i + 1];

        if (!isNaN(part)) {
            const index = parseInt(part, 10);
            const ordinal = getOrdinal(index);
            const section = parts[i - 1];

            switch (section) {
                case 'tenant':
                    result = `${ordinal} Tenant`;
                    if (nextPart) {
                        if (nextPart === 'email') {
                            result = `${ordinal} Tenant Email`;
                        } else if (nextPart === 'name') {
                            result = `${ordinal} Tenant Name`;
                        }
                    }
                    break;
                case 'deductions':
                    result = `${ordinal} Deduction`;
                    break;
                case 'approvals':
                    result = `${ordinal} Approval`;
                    break;
                case 'cc':
                    result = `${ordinal} CC Email`;
                    break;
                case 'email_details':
                    result = `${ordinal} Email Detail`;
                    if (nextPart) {
                        const fieldName = nextPart
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, (c) => c.toUpperCase());
                        result += ` ${fieldName}`;
                    }
                    break;
                default:
                    result = `${ordinal} Item`;
            }

            if (nextPart && section === 'tenant') {
                i++;
            } else if (nextPart) {
                const fieldName = nextPart
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, (c) => c.toUpperCase());
                result += ` ${fieldName}`;
            }
        } else {
            if (
                part === 'property_details' ||
                part === 'rental_period' ||
                part === 'account_details' ||
                part === 'statement_details' ||
                part === 'landlord_details' ||
                part === 'email_details' ||
                part === 'auditor_approval' ||
                part === 'address'
            ) {
                currentContext = part.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                continue;
            } else if (!result) {
                if (part === 'tenant') {
                    result = 'Tenants';
                } else {
                    result = part.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                }
            }
        }
    }

    return result || 'Field';
};

const groupErrors = (errors) => {
    const grouped = {
        Tenant: [],
        Address: [],
        RentalPeriod: [],
        PropertyDetails: [],
        AccountDetails: [],
        StatementDetails: [],
        LandlordDetails: [],
        EmailDetails: [],
        Approvals: [],
        AuditorApproval: [],
        Other: [],
    };

    errors.forEach(({ path, message }) => {
        const label = prettifyPath(path);
        const errorItem = {
            label,
            message: message
                .replace('is required', 'can’t be empty')
                .replace('must be positive', 'should be a positive number')
                .replace('must be at least 0', 'should be at least 0')
                .replace('Must be a valid email or empty', 'must be a valid email or empty')
        };

        if (path.startsWith('tenant')) {
            grouped.Tenant.push(errorItem);
        } else if (path.startsWith('address')) {
            grouped.Address.push(errorItem);
        } else if (path.startsWith('rental_period')) {
            grouped.RentalPeriod.push(errorItem);
        } else if (path.startsWith('property_details')) {
            grouped.PropertyDetails.push(errorItem);
        } else if (path.startsWith('account_details')) {
            grouped.AccountDetails.push(errorItem);
        } else if (path.startsWith('statement_details')) {
            grouped.StatementDetails.push(errorItem);
        } else if (path.startsWith('landlord_details')) {
            grouped.LandlordDetails.push(errorItem);
        } else if (path.startsWith('email_details')) {
            grouped.EmailDetails.push(errorItem);
        } else if (path.startsWith('cc')) {
            grouped.Other.push(errorItem); // CC is part of email_details
        } else if (path.startsWith('approvals')) {
            grouped.Approvals.push(errorItem);
        } else if (path.startsWith('auditor_approval')) {
            grouped.AuditorApproval.push(errorItem);
        } else {
            grouped.Other.push(errorItem);
        }
    });

    return grouped;
};

const validateStep = async (schema, values) => {
    try {
        await schema.validate(values, { abortEarly: false, context: values });
        return true;
    } catch (err) {
        const errors = err.inner?.map((e) => ({
            path: e.path || '',
            message: e.message,
        })) || [];

        const groupedErrors = groupErrors(errors);

        let html = '<p style="margin-bottom: 15px; font-size: 16px; color: #333;">Oops! A few fields need your attention before we can proceed. Here’s what to fix:</p>';
        html += `
      <div style="
        text-align: left;
        padding: 20px;
        background-color: #fef8e6;
        border: 2px solid #ffd700;
        border-radius: 10px;
        font-size: 14px;
        max-height: 400px;
        overflow-y: auto;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      ">
    `;

        Object.entries(groupedErrors).forEach(([section, errorList]) => {
            if (errorList.length > 0) {
                const sectionTitle = section.replace(/([A-Z])/g, ' $1').trim();
                html += `<div style="margin-top: 15px;">
          <h3 style="
            font-size: 16px;
            font-weight: 600;
            margin: 10px 0 8px;
            color: #b8860b;
            border-bottom: 1px solid #ddd;
            padding-bottom: 4px;
          ">${sectionTitle}</h3>`;

                errorList.forEach(({ label, message }, idx) => {
                    html += `<div style="margin-bottom: 10px; color: #333;">
            <span style="font-weight: 500; color: #b8860b; margin-right: 6px;">${idx + 1}.) ${label}</span>: ${message}
          </div>`;
                });

                html += '</div>';
            }
        });

        html += '</div>';

        if (errors.length > 0) {
            await Swal.fire({
                icon: 'warning',
                title: 'Let’s Fix These! 🚧',
                html,
                width: 900,
                confirmButtonText: 'Got It!',
                confirmButtonColor: '#34c38f',
                customClass: {
                    container: 'validation-error-modal',
                    title: 'validation-error-title text-xl font-bold text-gray-800',
                    htmlContainer: 'validation-error-content',
                    confirmButton: 'validation-error-button px-6 py-2 rounded-lg',
                },
            });
        }

        return false;
    }
};

const formatValidationErrors = (errors) => {
    return Object.entries(errors).map(([field, message]) => {
        const prettyField = prettifyPath(field);

        return `${prettyField}: ${message
            .replace('is required', 'can’t be empty')
            .replace('must be positive', 'should be a positive number')
            .replace('must be at least 1', 'should be at least 1')
            .replace('Must be a valid email or "N/A"', 'must be a valid email or "N/A"')}`;
    });
};

export { StatementOfAccountValidationSchema, validateStep, formatValidationErrors };